import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../dataManagement/uiDb/UiDb";
import { hideHotspotBanner } from "../../../../dataManagement/uiDb/GeneralDb";
import "./styles/HotspotBanner.css";

/**
 * HotspotBanner
 *
 * A fixed-position warning banner displayed when the user's location
 * is outside all configured hotspot areas (or when location permission
 * is denied). The banner is dismissible for the current session but
 * will reappear on page reload.
 */
const HotspotBanner: React.FC = () => {
    const dispatch = useDispatch();
    const isVisible = useSelector(
        (state: RootState) => state.general.isHotspotBannerVisible
    );

    // Draggable state
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ y: 0 });
    const bannerRef = React.useRef<HTMLDivElement>(null);

    // Mouse/Touch Handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({ y: e.clientY - position.y });
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        setIsDragging(true);
        setDragStart({ y: e.touches[0].clientY - position.y });
    };

    const handleMouseMove = React.useCallback((e: MouseEvent) => {
        if (!isDragging || !bannerRef.current) return;
        
        const newY = e.clientY - dragStart.y;
        const rect = bannerRef.current.getBoundingClientRect();
        const height = rect.height;
        
        // Bounds: Top edge (80px initial + y) >= 0 => y >= -70 (10px padding)
        // Bottom edge (80px + y + height) <= windowHeight => y <= windowHeight - 80 - height - 10
        const minY = -70; 
        const maxY = window.innerHeight - 80 - height - 10;
        
        const clampedY = Math.max(minY, Math.min(newY, maxY));
        
        setPosition(prev => ({ ...prev, y: clampedY }));
    }, [isDragging, dragStart]);

    const handleTouchMove = React.useCallback((e: TouchEvent) => {
        if (!isDragging || !bannerRef.current) return;
        
        const newY = e.touches[0].clientY - dragStart.y;
        const rect = bannerRef.current.getBoundingClientRect();
        const height = rect.height;
        
        const minY = -70; 
        const maxY = window.innerHeight - 80 - height - 10;
        
        const clampedY = Math.max(minY, Math.min(newY, maxY));
        
        setPosition(prev => ({ ...prev, y: clampedY }));
    }, [isDragging, dragStart]);

    const handleMouseUp = () => setIsDragging(false);

    // Attach global listeners while dragging
    React.useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleTouchMove]);

    const handleDismiss = () => {
        dispatch(hideHotspotBanner());
    };

    if (!isVisible) return null;

    return (
        <div
            className="bannerContainer"
            ref={bannerRef}
            style={{
                transform: `translate(-50%, ${position.y}px)`,
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none' // Prevent scrolling while dragging
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            <div className="bannerContent">
                <span className="bannerIcon">📍</span>
                <p className="bannerText">
                    We haven't launched in the selected area yet. Expect fewer or no listings, but feel free to browse, search, and list. Sign up and stay tuned for our launch!
                </p>
                <button
                    className="closeButton"
                    onClick={handleDismiss}
                    aria-label="Dismiss banner"
                >
                    ✕
                </button>
            </div>
        </div>
    );
};

export default HotspotBanner;
